import React, { useEffect } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { themeOrange } from "../../../utils/colorTheme";
import { useDispatch, useSelector } from "react-redux";
import {
  getPaymentRequestDataAsync,
  JobGetByVendorAsync,
} from "../../../services/services";
import { asyncStatus } from "../../../utils/asyncStatus";
import { getServiceLocalImage } from "../../../utils/serviceImages";
import { MdOutlinePayments, MdCheckCircle, MdCancel, MdPendingActions } from "react-icons/md";
import { FaCalendarAlt } from "react-icons/fa";

const statusConfig = {
  Released: {
    bg: "#e8f5e9",
    color: "#2e7d32",
    border: "#a5d6a7",
    stripe: "linear-gradient(90deg, #2e7d32 0%, #4CAF50 100%)",
    icon: <MdCheckCircle size={14} color="white" />,
    label: "RELEASED",
  },
  Requested: {
    bg: "#fff3e0",
    color: "#e65100",
    border: "#ffcc80",
    stripe: "linear-gradient(90deg, #e65100 0%, #FF9800 100%)",
    icon: <MdPendingActions size={14} color="white" />,
    label: "PAYMENT REQUESTED",
  },
  Declined: {
    bg: "#ffeaea",
    color: "#c62828",
    border: "#ef9a9a",
    stripe: "linear-gradient(90deg, #c62828 0%, #ef5350 100%)",
    icon: <MdCancel size={14} color="white" />,
    label: "DECLINED",
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" });
};

const PaymentRequest = () => {
  const dispatch = useDispatch();

  const {
    get_payment_request_data,
    get_payment_request_status,
  } = useSelector((state) => state.serviceManage);

  const loading = get_payment_request_status === asyncStatus.LOADING;

  // Summary counts
  const released = get_payment_request_data?.filter((i) => i?.payments_status === "Released").length || 0;
  const requested = get_payment_request_data?.filter((i) => i?.payments_status === "Requested").length || 0;
  const declined = get_payment_request_data?.filter((i) => i?.payments_status === "Declined").length || 0;
  const totalEarned = get_payment_request_data
    ?.filter((i) => i?.payments_status === "Released")
    .reduce((sum, i) => sum + (+i?.total_amount || 0), 0) || 0;

  useEffect(() => {
    dispatch(JobGetByVendorAsync());
    dispatch(getPaymentRequestDataAsync({ payments_status: "Requested" }));
  }, []);

  return (
    <Container maxWidth="xl">
      <Stack gap={3}>
        {/* Page header */}
        <Stack>
          <Typography sx={{ fontSize: "30px", fontWeight: "500", color: "#333", fontFamily: "Raleway" }}>
            Payment{" "}
            <Typography component="span" sx={{ fontSize: "30px", fontWeight: "700", color: "#333", fontFamily: "Raleway" }}>
              Requests
            </Typography>
          </Typography>
          <hr color="#D1D1D1" />
        </Stack>

        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }}>
            <CircularProgress size={30} sx={{ color: themeOrange }} />
          </Stack>
        ) : !get_payment_request_data?.length ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "60vh" }} spacing={2}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", backgroundColor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MdOutlinePayments size={36} color="#ccc" />
            </Box>
            <Typography sx={{ color: "#aaa", fontWeight: "500", fontSize: "16px" }}>
              No payment requests yet
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={3}>
            {/* Summary bar */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ flex: 1, p: 2, borderRadius: "12px", backgroundColor: "#e8f5e9", border: "1px solid #a5d6a7" }}>
                <Typography sx={{ fontSize: "22px", fontWeight: "800", color: "#2e7d32" }}>
                  ${totalEarned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#555", fontWeight: "500" }}>Total Earned</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, borderRadius: "12px", backgroundColor: "#fff3e0", border: "1px solid #ffcc80" }}>
                <Typography sx={{ fontSize: "22px", fontWeight: "800", color: "#e65100" }}>{requested}</Typography>
                <Typography sx={{ fontSize: "13px", color: "#555", fontWeight: "500" }}>Pending Requests</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, borderRadius: "12px", backgroundColor: "#f5f5f5", border: "1px solid #e0e0e0" }}>
                <Typography sx={{ fontSize: "22px", fontWeight: "800", color: "#555" }}>{released + requested + declined}</Typography>
                <Typography sx={{ fontSize: "13px", color: "#555", fontWeight: "500" }}>Total Requests</Typography>
              </Box>
            </Stack>

            {/* Payment cards */}
            <Stack spacing={2}>
              {get_payment_request_data.map((item, index) => {
                const status = item?.payments_status || "Requested";
                const cfg = statusConfig[status] || statusConfig["Requested"];
                const serviceName = item?.service?.name || "Service";

                return (
                  <Box
                    key={index}
                    sx={{
                      borderRadius: "14px",
                      border: "1.5px solid #ebebeb",
                      backgroundColor: "white",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    }}
                  >
                    {/* Status stripe */}
                    <Box sx={{ px: 3, py: 0.75, background: cfg.stripe, display: "flex", alignItems: "center", gap: 1 }}>
                      {cfg.icon}
                      <Typography sx={{ color: "white", fontSize: "12px", fontWeight: "700", letterSpacing: "0.5px" }}>
                        {cfg.label}
                      </Typography>
                    </Box>

                    {/* Card body */}
                    <Stack direction={{ xs: "column", sm: "row" }} sx={{ p: 2.5 }} gap={2} alignItems={{ sm: "center" }}>
                      {/* Service image */}
                      <Box
                        sx={{
                          width: { xs: "100%", sm: 90 },
                          height: { xs: 120, sm: 90 },
                          borderRadius: "10px",
                          overflow: "hidden",
                          flexShrink: 0,
                          filter: status === "Declined" ? "grayscale(60%)" : "grayscale(15%)",
                        }}
                      >
                        <img
                          src={getServiceLocalImage(serviceName)}
                          alt={serviceName}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </Box>

                      {/* Content */}
                      <Stack flex={1} spacing={1}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                          <Typography sx={{ fontWeight: "700", fontSize: "17px", color: "#1a1a1a", lineHeight: 1.3 }}>
                            {serviceName}
                          </Typography>
                          {item?.total_amount && (
                            <Typography
                              sx={{
                                fontWeight: "800",
                                fontSize: "20px",
                                color: status === "Released" ? "#2e7d32" : status === "Declined" ? "#999" : "#e65100",
                                flexShrink: 0,
                                textDecoration: status === "Declined" ? "line-through" : "none",
                              }}
                            >
                              ${(+item.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          )}
                        </Stack>

                        {item?.service?.description && (
                          <Typography sx={{ fontSize: "13px", color: "#888", lineHeight: 1.5 }}>
                            {item.service.description.length > 100
                              ? `${item.service.description.substring(0, 100)}...`
                              : item.service.description}
                          </Typography>
                        )}

                        {item?.date && (
                          <Stack direction="row" alignItems="center" gap={1}>
                            <FaCalendarAlt size={13} color="#999" style={{ flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "13px", color: "#666" }}>
                              {formatDate(item.date)}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>

                      {/* Right: status chip */}
                      <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} flexShrink={0}>
                        <Chip
                          label={status}
                          size="small"
                          sx={{
                            backgroundColor: cfg.bg,
                            color: cfg.color,
                            fontWeight: "700",
                            fontSize: "12px",
                            border: `1px solid ${cfg.border}`,
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Container>
  );
};

export default PaymentRequest;
